import className from 'classnames/bind';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'motion/react';
import BasicEmbed from './BasicEmbed';
import { IconButton } from "@components";
import { AcfMediaFragment } from '@graphqlTypes';
import ArrowRightIcon from "@/assets/svg/arrow-right.svg";
import { YTRef } from '../YoutubeEmbed/YoutubeEmbed';
import { PreviewVideoRef } from '../YoutubeEmbed/YoutubeVideoPreview';
import { EASE } from '@/core/constants';
import { getReduceMotion } from '@/utils/reduceMotion';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';

import styles from './BasicEmbed.module.scss';
const cx = className.bind(styles);

interface BasicEmbedVideoPreviewProps {
  videoUrl: string;
  previewVideoUrl: string;
  thumbnail: AcfMediaFragment;
  className?: string;
  playVideoLabel?: string;
}

const BasicEmbedVideoPreview = forwardRef<PreviewVideoRef, BasicEmbedVideoPreviewProps>(({ videoUrl, previewVideoUrl, thumbnail, className, playVideoLabel }, ref) => {
  const [showVideo, setShowVideo] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const videoShown = useRef(false);
  const videoRef = useRef<YTRef>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const videoAttached = useRef(false);
  const isInView = useInView(previewVideoRef);
  const posterImage = toProxiedWpUrl(thumbnail?.node?.mediaItemUrl);
  useImperativeHandle(ref, () => ({
    getPlayer: () => videoRef.current?.getPlayer(),
    showEmbed: () => setShowVideo(true),
  }));
  const onClick = () => {
    if (!videoShown.current) {
      videoShown.current = true;
      setShowVideo(true);
      const player = videoRef.current?.getPlayer();
      player?.playVideo();
    }
  };

  // handle parent's click
  useEffect(() => {
    if (!rootRef.current) return;
    const parent = rootRef.current.closest('button');
    if (parent) {
      parent.addEventListener('click', onClick);
    }
    return () => {
      if (parent) {
        parent.removeEventListener('click', onClick);
      }
    };
  }, []);

  // Determine if video should autoplay
  const [autoPlay, setAutoPlay] = useState<boolean>(false);
  useEffect(() => {
    setAutoPlay(!getReduceMotion());
  }, []);

  useEffect(() => {
    if (previewVideoRef.current && !videoAttached.current && isInView) {
      previewVideoRef.current.src = previewVideoUrl;
      videoAttached.current = true;
    }
    if (previewVideoRef.current && isInView && !showVideo && autoPlay) {
      previewVideoRef.current.play();
    } else {
      previewVideoRef.current?.pause();
    }
  }, [isInView, showVideo]);
  return (
    <div className={cx(["basic-embed", className])} onClick={onClick} ref={rootRef}>
      <AnimatePresence>
        {!showVideo &&
          <motion.div
            className={cx("basic-embed__overlay")}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE.OutQuad }}
          >
            <video ref={previewVideoRef} muted loop playsInline poster={posterImage} />
            <IconButton
              className={cx("play-button")}
              icon={ArrowRightIcon}
              onClick={onClick}
              size="large"
              ariaLabel={`Play video ${playVideoLabel || ''}`}
              tag="div"
            />
          </motion.div>
        }
      </AnimatePresence>
      <BasicEmbed videoUrl={videoUrl} ref={videoRef} />
    </div>
  )
});

export default BasicEmbedVideoPreview;